import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldExclamation, DatabaseAdd } from "react-bootstrap-icons";
import { useMerchantAuth, useMultiAuthCheck } from "../../../utilities/authCheck";
import { saveProductOption, addOptionListItem } from "../../../lib/webService/webService";
import { useUserContext } from "../../../context/UserContext";
import { toast } from "../../../utilities/toast";
import { use } from "framer-motion/client";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";
import { useFormDraft } from "../../../hooks/useFormDraft";

if (typeof document !== "undefined" && !document.getElementById("ao-styles")) {
  const s = document.createElement("style");
  s.id = "ao-styles";
  s.textContent = `
    @keyframes _ao_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._ao_sk { animation: _ao_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .ao-input { background: rgba(118,118,128,0.10) !important; border: 1px solid transparent !important; border-radius: 10px !important; padding: 11px 14px !important; font-size: 0.9375rem !important; color: #1c1c1e !important; transition: border-color .18s, box-shadow .18s !important; width: 100%; outline: none; font-family: inherit; }
    .ao-input:focus { border-color: #007AFF !important; box-shadow: 0 0 0 3px rgba(0,122,255,0.12) !important; background: rgba(118,118,128,0.08) !important; }
    .ao-input::placeholder { color: rgba(60,60,67,0.32) !important; }
    .ao-modal .modal-content { border-radius: 16px !important; border: none !important; overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,0.18) !important; }
  `;
  document.head.appendChild(s);
}

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';
const PAGE = { minHeight: "100vh", backgroundColor: "#F2F2F7", fontFamily: FONT };

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_ao_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const FieldLabel = ({ children }) => (
  <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 7 }}>
    {children}
  </label>
);

// ─── Add Item Modal ───────────────────────────────────────────────────────────

const AddItemModal = ({ setItemsArr, itemsArr }) => {
  const [show,   setShow]   = useState(false);
  const [value,  setValue]  = useState("");
  const [amount, setAmount] = useState(0);
  const [err,    setErr]    = useState({ status: false, message: "" });

  const handleClose = () => setShow(false);
  const handleShow  = () => setShow(true);

  const handleValueChange  = (e) => { setValue(e.target.value); };
  const handleAmountChange = (e) => { setAmount(Number(e.target.value)); };

  useEffect(() => {
    if (value != "") {
      setErr({ status: false, message: "" });
    }
  }, [value]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value === "") {
      setErr({ status: true, message: "Value is required" });
    } else {
      setErr({ status: false, message: "" });
      const data = { value, amount };
      let list = [data];
      for (var i = 0; i < itemsArr.length; i++) {
        list.push(itemsArr[i]);
      }
      setItemsArr(list);
      handleClose();
    }
  };

  return (
    <>
      <button
        onClick={handleShow}
        style={{
          display: "flex", alignItems: "center", gap: 7,
          padding: "8px 16px", borderRadius: 20,
          border: "1px solid rgba(60,60,67,.18)", background: "transparent",
          color: "rgba(60,60,67,.72)", fontSize: "0.8125rem", fontWeight: 500,
          cursor: "pointer", fontFamily: FONT,
        }}
      >
        <DatabaseAdd size={14} />
        Add items
      </button>

      <Modal show={show} onHide={handleClose} backdrop="static" centered dialogClassName="ao-modal">
        <Modal.Body style={{ padding: 0 }}>
          <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(60,60,67,.09)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", margin: 0, letterSpacing: "-0.02em" }}>New Item</h3>
            <button onClick={handleClose} style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(60,60,67,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "1.1rem", color: "rgba(60,60,67,.55)", lineHeight: 1 }}>×</button>
          </div>
          <div style={{ padding: "20px 24px" }}>
            {err.status && (
              <div style={{ backgroundColor: "rgba(255,59,48,.08)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: "0.875rem", color: "#FF3B30" }}>
                {err.message}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <FieldLabel>Value</FieldLabel>
                <input
                  type="text"
                  className="ao-input"
                  onChange={handleValueChange}
                  placeholder="Enter value"
                  required
                />
              </div>
              <div>
                <FieldLabel>Amount</FieldLabel>
                <input
                  type="number"
                  className="ao-input"
                  onChange={handleAmountChange}
                  placeholder="0"
                  required
                />
              </div>
            </div>
          </div>
          <div style={{ padding: "0 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={handleClose} style={{ padding: "9px 18px", borderRadius: 10, border: "1px solid rgba(60,60,67,.15)", background: "transparent", color: "rgba(60,60,67,.7)", fontSize: "0.875rem", fontWeight: 500, cursor: "pointer", fontFamily: FONT }}>
              Cancel
            </button>
            <button onClick={handleSubmit} style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: "#007AFF", color: "#fff", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
              Save item
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const AddOption = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { token } = useUserContext();
  const { isAuthorized, isLoading: authLoading, logout } = useMultiAuthCheck(["elite", "merchant"]);

  const [validated,  setValidated]  = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [name,       setName]       = useState("");
  const [title,      setTitle]      = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [itemsArr,   setItemsArr]   = useState([]);

  const { restoredDraft, clearDraft } = useFormDraft(`add-option:${productId}`, {
    name, title, isRequired, itemsArr,
  });

  useEffect(() => {
    if (restoredDraft) {
      setName(restoredDraft.name ?? "");
      setTitle(restoredDraft.title ?? "");
      setIsRequired(restoredDraft.isRequired ?? false);
      setItemsArr(restoredDraft.itemsArr ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   console.log("Items list: ", itemsArr);
  // }, [itemsArr]);

  const resetFields = () => {
    setTitle("");
    setName("");
  };

  const handleRadioChange = (e) => { setIsRequired(e.target.value); };
  const handleTitleChange = (e) => { setTitle(e.target.value); };
  const handleNameChange  = (e) => { setName(e.target.value); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (name === "" || title === "") {
      return;
    }

    setBtnLoading(true);
    const op = { title, name, required: isRequired };

    try {
      const data = await saveProductOption(op, productId, token);
      if (data.status === "success") {
        const optionId = data.message.split(" at ")[0].replace("#", "").trim();
        await addOptionListItem(itemsArr, productId, optionId, token);
        toast.success("Option added successfully!");
        resetFields();
        setItemsArr([]);
        setValidated(false);
        clearDraft();
      } else {
        throw new Error("Failed to add new option. Try again later.");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to add new option. Try again later.");
    } finally {
      setBtnLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div style={PAGE}>
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <Skel w={34} h={34} r={17} /><Skel w={120} h={22} r={7} />
          </div>
          <div style={{ backgroundColor: "#fff", borderRadius: 16, border: "1px solid rgba(60,60,67,.08)", padding: 20 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ marginBottom: 20 }}>
                <Skel w={60} h={11} r={5} style={{ marginBottom: 8 }} />
                <Skel w="100%" h={42} r={10} />
              </div>
            ))}
            <Skel w="100%" h={44} r={12} />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div style={PAGE}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "80vh", padding: 32 }}>
          <div style={{ width: 60, height: 60, borderRadius: 18, backgroundColor: "rgba(255,149,0,.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <ShieldExclamation size={28} color="#FF9500" />
          </div>
          <h3 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 6, textAlign: "center" }}>Access Restricted</h3>
          <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,.5)", textAlign: "center", marginBottom: 24 }}>Merchant authentication required.</p>
          <button onClick={logout} style={{ padding: "11px 28px", borderRadius: 12, border: "none", backgroundColor: "#007AFF", color: "#fff", fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
            Sign In as Merchant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE}>
      <Seo {...generateNoIndexSEO("Add Option")} />
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px 48px" }}>

        {/* Back nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(60,60,67,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#1c1c1e", flexShrink: 0 }}>
            <ArrowLeft size={16} />
          </button>
          <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.03em", margin: 0 }}>Add Option</h2>
        </div>

        <div style={{ backgroundColor: "#fff", borderRadius: 16, border: "1px solid rgba(60,60,67,.09)", padding: "20px 24px", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
          <form noValidate onSubmit={handleSubmit}>

            {/* Title + Name row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <FieldLabel>Title</FieldLabel>
                <input
                  type="text"
                  className="ao-input"
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Enter title"
                  required
                  style={{ borderColor: validated && !title ? "#FF3B30" : undefined }}
                />
                {validated && !title && (
                  <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a title</div>
                )}
              </div>
              <div>
                <FieldLabel>Name</FieldLabel>
                <input
                  type="text"
                  className="ao-input"
                  value={name}
                  onChange={handleNameChange}
                  placeholder="Enter name"
                  required
                  style={{ borderColor: validated && !name ? "#FF3B30" : undefined }}
                />
                {validated && !name && (
                  <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a name</div>
                )}
              </div>
            </div>

            {/* Required toggle */}
            <div style={{ marginBottom: 20 }}>
              <FieldLabel>Required?</FieldLabel>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => handleRadioChange({ target: { value: true } })}
                  style={{
                    padding: "8px 20px", borderRadius: 10, border: "none", cursor: "pointer",
                    fontSize: "0.875rem", fontWeight: 600, fontFamily: FONT,
                    backgroundColor: isRequired === true || isRequired === "true" ? "#007AFF" : "rgba(118,118,128,0.10)",
                    color: isRequired === true || isRequired === "true" ? "#fff" : "rgba(60,60,67,0.7)",
                    transition: "background-color .15s, color .15s",
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => handleRadioChange({ target: { value: false } })}
                  style={{
                    padding: "8px 20px", borderRadius: 10, border: "none", cursor: "pointer",
                    fontSize: "0.875rem", fontWeight: 600, fontFamily: FONT,
                    backgroundColor: isRequired === false || isRequired === "false" ? "#007AFF" : "rgba(118,118,128,0.10)",
                    color: isRequired === false || isRequired === "false" ? "#fff" : "rgba(60,60,67,0.7)",
                    transition: "background-color .15s, color .15s",
                  }}
                >
                  No
                </button>
              </div>
            </div>

            {/* Option Items heading + add button */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, paddingTop: 4 }}>
              <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e", letterSpacing: "-0.015em" }}>Option Items</div>
              <AddItemModal setItemsArr={setItemsArr} itemsArr={itemsArr} />
            </div>

            {/* Items display */}
            {itemsArr.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                {itemsArr.map((item, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "5px 12px", borderRadius: 20,
                      backgroundColor: "rgba(60,60,67,.07)",
                      fontSize: "0.8125rem", fontWeight: 500,
                      color: "rgba(60,60,67,.75)",
                    }}
                  >
                    {item.value}
                    {item.amount > 0 && (
                      <span style={{ color: "#34C759", fontWeight: 600 }}>+{item.amount}</span>
                    )}
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ marginBottom: 20, padding: "12px 14px", borderRadius: 12, backgroundColor: "rgba(60,60,67,.04)", textAlign: "center" }}>
                <span style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,.38)" }}>No items added yet</span>
              </div>
            )}

            <button
              type="submit"
              disabled={btnLoading}
              style={{
                width: "100%", padding: "13px", borderRadius: 12, border: "none",
                backgroundColor: btnLoading ? "rgba(0,122,255,.5)" : "#007AFF",
                color: "#fff", fontSize: "0.9375rem", fontWeight: 600,
                cursor: btnLoading ? "not-allowed" : "pointer",
                fontFamily: FONT, transition: "background-color .18s",
              }}
            >
              {btnLoading ? "Submitting…" : "Submit"}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
};

export default AddOption;
